/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { Launch, Add } from '@carbon/react/icons';
import {
  Button,
  Dropdown,
  Tooltip,
  FormGroup,
  RadioButtonGroup,
  RadioButton,
  RadioButtonSkeleton,
  InlineLoading,
  InlineNotification
} from 'carbon-components-react';
import cx from 'classnames';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { Fragment, useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import Connect from '@ibm-watson/shared/assets/images/connect.svg';
import { generateDiscoveryLink } from '@ibm-watson/shared/modules/discovery/util/search-skill';
import { getAssistantId } from '@ibm-watson/shared/modules/ia/store/session/selectors';
import { isCpd } from '@ibm-watson/shared/modules/ia/util/config';
import { ctaEvents } from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import { FormattedMessageWithLink } from '../../../channels/helpers/linkHelpers';
import Link from '../../../ia/components/link/Link';
import {
  listInstancesIfRequired,
  listProjectsIfRequired
} from '../../store/actions';
import { selectInstanceId, selectProjectId } from '../../store/reducer';
import {
  selectAreProjectsLoading,
  selectAreInstancesLoading,
  selectSelectedInstanceId,
  selectSelectedProjectId,
  selectUnavailableInstances,
  selectAllV2Instances,
  selectProjectsByInstanceId,
  selectInstanceById,
  selectedProjectHasCollection
} from '../../store/selectors';
import { InstanceLabel } from './InstanceLabel';
import { messages } from './messages';

import './SearchInstanceConfig.scss';

export const SearchInstanceConfig = ({ waEnvironmentId }) => {
  const { formatMessage: t } = useIntl();
  const dispatch = useDispatch();

  const draftAssistantId = useSelector(getAssistantId);

  // Instances
  const areInstancesLoading = useSelector(selectAreInstancesLoading);
  const instances = useSelector(selectAllV2Instances);
  const selectedInstanceId = useSelector(selectSelectedInstanceId);
  const selectedInstance = useSelector(
    state => selectInstanceById(state, selectedInstanceId),
    shallowEqual
  );
  const unavailable = useSelector(selectUnavailableInstances);

  // Projects
  const areProjectsLoading = useSelector(selectAreProjectsLoading);
  const projects = useSelector(
    state => selectProjectsByInstanceId(state, selectedInstanceId),
    shallowEqual
  );
  const selectedProjectId = useSelector(selectSelectedProjectId);
  const projectHasCollection = useSelector(selectedProjectHasCollection);

  useEffect(() => {
    if (draftAssistantId) {
      dispatch(listInstancesIfRequired());
    }
  }, [dispatch, draftAssistantId]);

  useEffect(() => {
    if (!selectedInstanceId && instances && instances[0]) {
      dispatch(selectInstanceId(instances[0].id));
    }
  }, [dispatch, instances, selectedInstanceId]);

  useEffect(() => {
    if (selectedInstance && !selectedInstance.projects) {
      dispatch(listProjectsIfRequired(selectedInstance.id));
    }
  }, [dispatch, selectedInstance]);

  useEffect(() => {
    if (!selectedProjectId && projects && projects[0]) {
      dispatch(selectProjectId(projects[0].project_id));
    }
  }, [dispatch, projects, selectedProjectId]);

  const mapInstanceItem = instance => {
    if (!instance) {
      return null;
    }

    return {
      ...instance,
      id: instance.id,
      label: <InstanceLabel instance={instance} />,
      name: instance.name || instance.entity.name
    };
  };

  const handleDropdownSelect = useCallback(
    ({ selectedItem }) => {
      if (selectedItem.id !== selectedInstance?.id) {
        dispatch(selectInstanceId(selectedItem.id));
      }
    },
    [dispatch, selectedInstance]
  );

  const handleCollectionChange = useCallback(
    projectId => {
      if (projectId !== selectedProjectId) {
        dispatch(selectProjectId(projectId));
      }
    },
    [dispatch, selectedProjectId]
  );

  const onOpenInDiscoveryClick = useCallback(() => {
    ctaEvents.searchOpenInDiscovery();
  }, []);

  const onCreateProjectClick = useCallback(() => {
    ctaEvents.searchCreateProject();
  }, []);

  const createDiscoveryInstance = useCallback(() => {
    ctaEvents.searchCreateDiscovery();
  }, []);

  const generateSkillDataForLink = useCallback(
    (environmentId, collectionId, projectId) => {
      //TEMP TODO!! ?need skill??
      const result = {};
      if (instances.length > 0 && selectedInstance) {
        result.config = { ...result.config, instance_id: selectedInstance.id };
      }
      if (collectionId !== undefined) {
        result.config = { ...result.config, collection_id: collectionId };
      }
      if (environmentId !== undefined) {
        result.config = { ...result.config, environment_id: environmentId };
      }
      if (projectId !== undefined) {
        result.config = { ...result.config, project_id: projectId };
      }
      if (waEnvironmentId !== undefined) {
        result.config = { ...result.config, waEnvironmentId };
      }
      return result;
    },
    [instances.length, selectedInstance, waEnvironmentId]
  );

  const createProjectDiscoveryLink = useMemo(() => {
    //for beta only V2 so true for this function for now (orig: isInstanceV2)
    const isV2 = true; //isSelectedInstanceV2
    return (
      generateDiscoveryLink(
        selectedInstance,
        window.location,
        'connection',
        generateSkillDataForLink(null, null, isV2 && 'new'),
        true
      ) + '&ftue_flag=true'
    );
  }, [generateSkillDataForLink, selectedInstance]);

  const instanceUnavailable = selectedInstance && selectedInstance.unavailable;

  const createProjectsSection = () => {
    const { projects = [] } = selectedInstance;
    if (projects.length === 0) {
      return (
        <div className="no-collections-create-collection-container">
          <div className="no-collections-info">
            <img
              src={Connect}
              className="no-collections-icon"
              height="120"
              width="120"
              alt={t(messages.createNewProjectBringData)}
            />
            <div className="no-collections-body">
              {t(messages.noProjectsYet)} <br />
              {t(messages.createNewProjectBringData)}
            </div>
          </div>
        </div>
      );
    }
  };

  const projectCollectionSection = () => {
    if (selectedInstance && !instanceUnavailable && instances.length > 0) {
      if (
        areInstancesLoading ||
        (areProjectsLoading &&
          (!_.get(selectedInstance, 'collections.length') ||
            !_.get(selectedInstance, 'projects')))
      ) {
        return (
          <RadioButtonSkeleton className="projects-collections-group-loading-indicator" />
        );
      } else if (
        !areProjectsLoading ||
        _.get(selectedInstance, 'collections.length') ||
        _.get(selectedInstance, 'projects')
      ) {
        const { projects = [] } = selectedInstance;
        const showNoProjectError = projects.length > 0 && !projectHasCollection;
        return (
          <div className="projects-collections-group-container">
            <FormGroup
              legendText={
                <SectionTitle
                  title={t(messages.chooseProjectLabel)}
                  toolTipText={t(messages.chooseProjectTooltip)}
                />
              }>
              <div className="projects-collections-section-header">
                {!selectedInstance.collectionsLimitReached && !isCpd && (
                  <Button
                    href={createProjectDiscoveryLink}
                    kind="ghost"
                    disabled={selectedInstance?.tooling_url}
                    onClick={onCreateProjectClick}>
                    <FormattedMessage {...messages.createNewProject} />
                    <Add size={16} />
                  </Button>
                )}
              </div>
              {showNoProjectError && (
                <InlineNotification
                  className="no-collection-notification--error"
                  kind="error"
                  title=""
                  subtitle={t(messages.missingCollection)}
                  iconDescription={t(messages.closeErrorDescription)}
                  statusIconDescription={t(messages.closeErrorDescription)}
                />
              )}
              <div
                className={cx('details-header', {
                  'details-header__with-error': showNoProjectError
                })}>
                {selectedInstance.projects && (
                  <div className="projects-header">
                    <FormattedMessage {...messages.projectName} />
                  </div>
                )}
                <FormattedMessage {...messages.collectionName} />
              </div>
              <RadioButtonGroup
                className="projects-collections-group"
                name="discover-projects-collections-group"
                valueSelected={selectedProjectId}
                legendText={t(messages.discoveryProjects)}
                onChange={handleCollectionChange}>
                {projects.map(({ project_id, name, collections }) => (
                  <RadioButton
                    key={project_id}
                    value={project_id}
                    id={project_id}
                    labelText={
                      <div className="projects-wrapper">
                        <div className="project-section">
                          <div className="project-name">{name}</div>
                        </div>
                        <div className="project-collections">
                          {collections.map(({ name, collection_id }) => (
                            <div
                              className="project-collection"
                              key={collection_id}>
                              {name}
                            </div>
                          ))}
                        </div>
                        <Link
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={onOpenInDiscoveryClick}
                          href={generateDiscoveryLink(
                            selectedInstance,
                            window.location,
                            'connection',
                            generateSkillDataForLink(null, null, project_id),
                            false
                          )}
                          disabled={isCpd && !selectedInstance?.tooling_url}>
                          <Launch
                            size={16}
                            className="bx--btn__icon"
                            aria-label={t(messages.openInDiscovery)}
                          />
                        </Link>
                      </div>
                    }
                  />
                ))}
                {selectedInstance.collections.map(
                  ({ collection_id, name, environment_id }) => (
                    <RadioButton
                      key={`collection-key-${collection_id}`}
                      value={collection_id}
                      id={`collection-key-${collection_id}`}
                      labelText={
                        <div className="collection-text-wrapper">
                          {name}
                          <Link
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={onOpenInDiscoveryClick}
                            href={generateDiscoveryLink(
                              selectedInstance,
                              window.location,
                              'connection',
                              generateSkillDataForLink(
                                environment_id,
                                collection_id
                              ),
                              false
                            )}
                            disabled={
                              isCpd && !_.get(selectedInstance, 'tooling_url')
                            }>
                            <Launch
                              size={16}
                              className="bx--btn__icon"
                              aria-label={t(messages.openInDiscovery)}
                            />
                          </Link>
                        </div>
                      }
                    />
                  )
                )}
              </RadioButtonGroup>
            </FormGroup>
            {createProjectsSection()}
          </div>
        );
      }
      return null;
    }
    return null;
  };

  return (
    <Fragment>
      <div className="SearchInstance">
        <div className="SearchInstance__content-container">
          <div className="SearchInstance__instruction">
            <div className="TabContent__link-larger-text">
              <FormattedMessageWithLink
                url={
                  'https://cloud.ibm.com/docs/discovery-data?topic=discovery-data-about'
                }
                {...messages.discoDescription}
              />
            </div>
          </div>
          <div className="SearchInstance__dropdown-row">
            <Dropdown
              id="instances-dropdown"
              className="instances-dropdown"
              label="Instance name"
              titleText={
                <SectionTitle
                  title={t(messages.chooseDiscoLabel)}
                  toolTipText={t(messages.chooseDiscoTooltip)}
                />
              }
              items={instances.map(mapInstanceItem)}
              selectedItem={mapInstanceItem(selectedInstance)}
              onChange={handleDropdownSelect}></Dropdown>
            {areInstancesLoading && (
              <InlineLoading description={t(messages.loadingInstances)} />
            )}
            {unavailable.length > 0 && (
              <InlineNotification
                className="no-credentials-notification--warning"
                kind="warning-alt"
                title=""
                subtitle={t(messages.missingCreds)}
                iconDescription="close warning"
                statusIconDescription="close warning"
              />
            )}
          </div>
          {!isCpd && (
            <p className="create-instance-desc">
              <div className="TabContent__link-larger-text">
                <FormattedMessageWithLink
                  url={
                    'https://cloud.ibm.com/catalog/services/watson-discovery'
                  }
                  onClick={createDiscoveryInstance}
                  {...messages.createAnother}
                />
              </div>
            </p>
          )}
          {projectCollectionSection()}
        </div>
      </div>
    </Fragment>
  );
};

SearchInstanceConfig.propTypes = {
  waEnvironmentId: PropTypes.string
};

const SectionTitle = ({ title, toolTipText }) => (
  <Tooltip
    align="end"
    id={`SearchInstance__tooltip--${title}`}
    triggerText={title}>
    {toolTipText}
  </Tooltip>
);

SectionTitle.propTypes = {
  title: PropTypes.string,
  toolTipText: PropTypes.string
};
